/*
 * Campanhas e propagandas.
 *
 * As propagandas de uma campanha formam a sequencia de stories que o cliente
 * ve no portal, na ordem definida aqui.
 */
import { useEffect, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { enviarImagem } from '../lib/upload';
import { useAuth } from '../lib/auth';
import type { AdDto, CampaignDto, MikrotikDto } from '../lib/types';
import { Alert, Button, Card, Field, Spinner } from '../components/ui';

const FORM_INICIAL = {
  title: '',
  description: '',
  startsAt: '',
  endsAt: '',
  priority: '0',
  unitId: '',
  mikrotikIds: [] as number[],
};

export function CampaignsPage() {
  const { can } = useAuth();
  const podeEscrever = can(PERMISSIONS.CAMPAIGN_WRITE);

  const [campanhas, setCampanhas] = useState<CampaignDto[] | null>(null);
  const [unidades, setUnidades] = useState<{ id: number; name: string }[]>([]);
  const [equipamentos, setEquipamentos] = useState<MikrotikDto[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const [formAberto, setFormAberto] = useState(false);
  const [form, setForm] = useState(FORM_INICIAL);
  const [formError, setFormError] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  /** Campanha com o gerenciador de propagandas aberto. */
  const [expandida, setExpandida] = useState<number | null>(null);
  const [propagandas, setPropagandas] = useState<AdDto[]>([]);
  const [carregandoAds, setCarregandoAds] = useState(false);
  const [enviando, setEnviando] = useState(false);

  async function carregar() {
    setErro(null);
    try {
      const [lista, opcoes, mikrotiks] = await Promise.all([
        apiList<CampaignDto>('/admin/campaigns'),
        apiRequest<{ id: number; name: string }[]>('/admin/units/options').catch(() => []),
        apiList<MikrotikDto>('/admin/mikrotiks').catch(() => ({ data: [], meta: undefined })),
      ]);
      setCampanhas(lista.data);
      setUnidades(opcoes);
      setEquipamentos(mikrotiks.data);
    } catch (caught) {
      setCampanhas([]);
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
    }
  }

  useEffect(() => {
    void carregar();
  }, []);

  async function abrirPropagandas(campanha: CampaignDto) {
    if (expandida === campanha.id) {
      setExpandida(null);
      return;
    }
    setExpandida(campanha.id);
    setCarregandoAds(true);
    try {
      setPropagandas(await apiRequest<AdDto[]>(`/admin/campaigns/${campanha.id}/ads`));
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
    } finally {
      setCarregandoAds(false);
    }
  }

  async function criar(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSalvando(true);
    try {
      await apiRequest('/admin/campaigns', {
        method: 'POST',
        body: {
          title: form.title,
          description: form.description || null,
          // datetime-local nao traz fuso; converte para ISO com o fuso local.
          startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
          endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
          priority: Number(form.priority) || 0,
          unitId: form.unitId ? Number(form.unitId) : null,
          mikrotikIds: form.mikrotikIds,
        },
      });
      setForm(FORM_INICIAL);
      setFormAberto(false);
      await carregar();
    } catch (caught) {
      setFormError(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao criar');
    } finally {
      setSalvando(false);
    }
  }

  async function enviarPropaganda(campaignId: number, arquivo: File) {
    setEnviando(true);
    setErro(null);
    try {
      const midia = await enviarImagem(arquivo);
      await apiRequest(`/admin/campaigns/${campaignId}/ads`, {
        method: 'POST',
        body: { mediaId: midia.id },
      });
      setPropagandas(await apiRequest<AdDto[]>(`/admin/campaigns/${campaignId}/ads`));
      await carregar();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha no envio');
    } finally {
      setEnviando(false);
    }
  }

  async function atualizarPropaganda(ad: AdDto, mudanca: Partial<AdDto>) {
    try {
      await apiRequest(`/admin/ads/${ad.id}`, { method: 'PATCH', body: mudanca });
      setPropagandas(await apiRequest<AdDto[]>(`/admin/campaigns/${ad.campaignId}/ads`));
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao atualizar');
    }
  }

  async function moverPropaganda(ad: AdDto, direcao: -1 | 1) {
    const indice = propagandas.findIndex((item) => item.id === ad.id);
    const destino = indice + direcao;
    if (destino < 0 || destino >= propagandas.length) return;

    const ordem = propagandas.map((item) => item.id);
    [ordem[indice], ordem[destino]] = [ordem[destino] as number, ordem[indice] as number];

    try {
      setPropagandas(
        await apiRequest<AdDto[]>(`/admin/campaigns/${ad.campaignId}/ads/order`, {
          method: 'PUT',
          body: { adIds: ordem },
        }),
      );
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao reordenar');
    }
  }

  async function removerPropaganda(ad: AdDto) {
    if (!window.confirm('Remover esta propaganda?')) return;
    try {
      await apiRequest(`/admin/ads/${ad.id}`, { method: 'DELETE' });
      setPropagandas(await apiRequest<AdDto[]>(`/admin/campaigns/${ad.campaignId}/ads`));
      await carregar();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao remover');
    }
  }

  async function removerCampanha(campanha: CampaignDto) {
    if (!window.confirm(`Remover a campanha "${campanha.title}"?`)) return;
    try {
      await apiRequest(`/admin/campaigns/${campanha.id}`, { method: 'DELETE' });
      if (expandida === campanha.id) setExpandida(null);
      await carregar();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao remover');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Campanhas</h1>
          <p className="text-sm text-slate-500">
            As propagandas aparecem como stories no portal, na ordem definida aqui.
          </p>
        </div>

        {podeEscrever ? (
          <Button onClick={() => setFormAberto((aberto) => !aberto)}>
            {formAberto ? 'Cancelar' : '+ Nova campanha'}
          </Button>
        ) : null}
      </div>

      {erro ? <Alert>{erro}</Alert> : null}

      {podeEscrever && formAberto ? (
        <Card title="Nova campanha">
          <form onSubmit={criar} className="grid gap-4 sm:grid-cols-2">
            {formError ? (
              <div className="sm:col-span-2">
                <Alert>{formError}</Alert>
              </div>
            ) : null}

            <Field
              label="Título"
              value={form.title}
              onChange={(event) => setForm({ ...form, title: event.target.value })}
              required
              minLength={2}
            />
            <Field
              label="Descrição"
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
            <Field
              label="Início da vigência"
              type="datetime-local"
              value={form.startsAt}
              onChange={(event) => setForm({ ...form, startsAt: event.target.value })}
              hint="Vazio = vale desde já."
            />
            <Field
              label="Fim da vigência"
              type="datetime-local"
              value={form.endsAt}
              onChange={(event) => setForm({ ...form, endsAt: event.target.value })}
              hint="Vazio = sem prazo."
            />
            <Field
              label="Prioridade"
              type="number"
              value={form.priority}
              onChange={(event) => setForm({ ...form, priority: event.target.value })}
              min={0}
              hint="Maior número aparece antes na sequência."
            />

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">Unidade</span>
              <select
                value={form.unitId}
                onChange={(event) => setForm({ ...form, unitId: event.target.value })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              >
                <option value="">Todas as unidades</option>
                {unidades.map((unidade) => (
                  <option key={unidade.id} value={unidade.id}>
                    {unidade.name}
                  </option>
                ))}
              </select>
            </label>

            {equipamentos.length > 0 ? (
              <div className="sm:col-span-2">
                <p className="mb-1.5 text-sm font-medium text-slate-700">
                  Restringir a equipamentos específicos
                </p>
                <p className="mb-2 text-xs text-slate-500">
                  Sem seleção, a campanha vale para toda a unidade escolhida (ou para toda a rede).
                </p>
                <div className="grid gap-1.5 sm:grid-cols-3">
                  {equipamentos.map((equipamento) => (
                    <label
                      key={equipamento.id}
                      className="flex items-center gap-2 rounded border border-slate-300 p-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form.mikrotikIds.includes(equipamento.id)}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            mikrotikIds: event.target.checked
                              ? [...form.mikrotikIds, equipamento.id]
                              : form.mikrotikIds.filter((id) => id !== equipamento.id),
                          })
                        }
                        className="size-4 rounded border-slate-300"
                      />
                      {equipamento.name}
                    </label>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="sm:col-span-2">
              <Button type="submit" disabled={salvando}>
                {salvando ? 'Criando...' : 'Criar campanha'}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card title="Campanhas cadastradas">
        {campanhas === null ? (
          <Spinner />
        ) : campanhas.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nenhuma campanha. Sem campanhas, o portal mostra apenas o botão de conectar.
          </p>
        ) : (
          <div className="space-y-3">
            {campanhas.map((campanha) => (
              <div key={campanha.id} className="rounded-lg border border-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-2 p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">{campanha.title}</span>
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                          campanha.isRunning
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {campanha.isRunning ? 'em exibição' : 'parada'}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {campanha.adCount} propaganda{campanha.adCount === 1 ? '' : 's'}
                      {campanha.unitName ? ` · ${campanha.unitName}` : ' · todas as unidades'}
                      {campanha.mikrotikIds.length > 0
                        ? ` · ${campanha.mikrotikIds.length} equipamento(s)`
                        : ''}
                      {campanha.priority > 0 ? ` · prioridade ${campanha.priority}` : ''}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => void abrirPropagandas(campanha)}>
                      {expandida === campanha.id ? 'Fechar' : 'Propagandas'}
                    </Button>
                    {podeEscrever ? (
                      <Button variant="ghost" onClick={() => void removerCampanha(campanha)}>
                        Remover
                      </Button>
                    ) : null}
                  </div>
                </div>

                {expandida === campanha.id ? (
                  <div className="border-t border-slate-200 bg-slate-50 p-3">
                    {carregandoAds ? (
                      <Spinner />
                    ) : (
                      <div className="space-y-3">
                        {propagandas.length === 0 ? (
                          <p className="text-sm text-slate-500">
                            Nenhuma propaganda nesta campanha.
                          </p>
                        ) : (
                          <ol className="space-y-2">
                            {propagandas.map((ad, indice) => (
                              <li
                                key={ad.id}
                                className="flex flex-wrap items-center gap-3 rounded border border-slate-200 bg-white p-2"
                              >
                                <span className="w-6 text-center text-xs font-medium text-slate-500">
                                  {indice + 1}
                                </span>

                                <img
                                  src={ad.mediaUrl}
                                  alt=""
                                  className="h-16 w-10 rounded object-cover"
                                />

                                <div className="flex flex-1 flex-wrap items-center gap-3 text-xs">
                                  <label className="flex items-center gap-1">
                                    <span className="text-slate-600">Tempo</span>
                                    <input
                                      type="number"
                                      min={1}
                                      max={60}
                                      value={ad.minViewSeconds}
                                      onChange={(event) =>
                                        void atualizarPropaganda(ad, {
                                          minViewSeconds: Number(event.target.value),
                                        })
                                      }
                                      className="w-14 rounded border border-slate-300 px-1.5 py-1"
                                      disabled={!podeEscrever}
                                    />
                                    <span className="text-slate-500">s</span>
                                  </label>

                                  <label className="flex items-center gap-1 text-slate-600">
                                    <input
                                      type="checkbox"
                                      checked={ad.isRequired}
                                      onChange={(event) =>
                                        void atualizarPropaganda(ad, {
                                          isRequired: event.target.checked,
                                        })
                                      }
                                      disabled={!podeEscrever}
                                      className="size-3.5 rounded border-slate-300"
                                    />
obrigatória
                                  </label>

                                  <label className="flex items-center gap-1 text-slate-600">
                                    <input
                                      type="checkbox"
                                      checked={ad.allowSkip}
                                      onChange={(event) =>
                                        void atualizarPropaganda(ad, {
                                          allowSkip: event.target.checked,
                                        })
                                      }
                                      disabled={!podeEscrever}
                                      className="size-3.5 rounded border-slate-300"
                                    />
                                    pode pular
                                  </label>
                                </div>

                                {podeEscrever ? (
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      onClick={() => void moverPropaganda(ad, -1)}
                                      disabled={indice === 0}
                                      aria-label="Mover para cima"
                                    >
                                      ↑
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      onClick={() => void moverPropaganda(ad, 1)}
                                      disabled={indice === propagandas.length - 1}
                                      aria-label="Mover para baixo"
                                    >
                                      ↓
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      onClick={() => void removerPropaganda(ad)}
                                    >
                                      Remover
                                    </Button>
                                  </div>
                                ) : null}
                              </li>
                            ))}
                          </ol>
                        )}

                        {podeEscrever ? (
                          <div>
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-400 px-3 py-2 text-sm text-slate-600 hover:bg-white">
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                className="hidden"
                                disabled={enviando}
                                onChange={(event) => {
                                  const arquivo = event.target.files?.[0];
                                  if (arquivo) void enviarPropaganda(campanha.id, arquivo);
                                  event.target.value = '';
                                }}
                              />
                              {enviando ? 'Enviando...' : '+ Adicionar imagem'}
                            </label>
                            <p className="mt-1 text-xs text-slate-500">
                              JPG, PNG ou WebP até 10 MB. Formato vertical (9:16) fica melhor no
                              celular.
                            </p>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
